import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createDentalChart,
  createPerioChart,
  deleteDentalChartById,
  deletePerioChartById,
  getChartsByPatientId,
  updateDentalChartById,
  updatePerioChartById,
} from '@services/api/charting.api'
import {
  IChartingData,
  IDentalChart,
  IPerioChart,
} from '@dataTypes/chartingFeature.types'
import type { ChartSaveSource } from '@utils/chart-save.utils'

export interface ChartPersistRequest<T> {
  chart: T
  saveSource?: ChartSaveSource
}

export const fetchChartingbyPatientId = createAsyncThunk<
  IChartingData,
  string,
  { rejectValue: string }
>(
  'api/charting/fetch/ChartingbyPatientId',
  async (patientId, { rejectWithValue }) => {
    try {
      if (patientId.length === 0) return rejectWithValue('Invalid patient ID')
      const response = await getChartsByPatientId(patientId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      if (response.data && typeof response.data === 'object') {
        return response.data as IChartingData
      }

      return rejectWithValue('Chart details not found')
    } catch (err) {
      return rejectWithValue('Failed to fetch charts')
    }
  },
)

export const addPerioChart = createAsyncThunk<
  IPerioChart,
  ChartPersistRequest<IPerioChart>,
  { rejectValue: string }
>(
  'api/charting/add/perio_chart',
  async ({ chart, saveSource = 'manual' }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createPerioChart(
        {
          ...chart,
          created_by: user.user_id,
          updated_by: user.user_id,
        },
        saveSource,
      )
      if (response.statusCode !== 201) {
        return rejectWithValue(response.message || 'Failed to fetch charts')
      }
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          return response.data[0] as IPerioChart
        }
        return response.data as IPerioChart
      }
      return rejectWithValue('Charts not found in the response')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create chart'
      return rejectWithValue(message)
    }
  },
)

export const addDentalChart = createAsyncThunk<
  IDentalChart,
  ChartPersistRequest<IDentalChart>,
  { rejectValue: string }
>(
  'api/charting/add/dental_chart',
  async ({ chart, saveSource = 'manual' }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createDentalChart(
        {
          ...chart,
          created_by: user.user_id,
          updated_by: user.user_id,
        },
        saveSource,
      )
      if (response.statusCode !== 201) {
        return rejectWithValue(response.message || 'Failed to fetch charts')
      }
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          return response.data[0] as IDentalChart
        }
        return response.data as IDentalChart
      }
      return rejectWithValue('Charts not found in the response')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create charts'
      return rejectWithValue(message)
    }
  },
)

export const updateDentalChart = createAsyncThunk<
  IDentalChart,
  { id: string; chart: IDentalChart; saveSource?: ChartSaveSource },
  { rejectValue: string }
>(
  'api/charting/update/dental/chart',
  async ({ id, chart, saveSource = 'manual' }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateDentalChartById(
        id,
        {
          ...chart,
          updated_by: user.user_id,
        },
        saveSource,
      )
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to fetch charts')
      }
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          return response.data[0] as IDentalChart
        }
        return response.data as IDentalChart
      }
      return rejectWithValue('Charts not found in the response')
    } catch {
      return rejectWithValue('Failed to update charts')
    }
  },
)

export const updatePerioChart = createAsyncThunk<
  IPerioChart,
  { id: string; chart: IPerioChart; saveSource?: ChartSaveSource },
  { rejectValue: string }
>(
  'api/charting/update/perio/chart',
  async ({ id, chart, saveSource = 'manual' }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updatePerioChartById(
        id,
        {
          ...chart,
          updated_by: user.user_id,
        },
        saveSource,
      )
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to fetch charts')
      }
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          return response.data[0] as IPerioChart
        }
        return response.data as IPerioChart
      }
      return rejectWithValue('Charts not found in the response')
    } catch {
      return rejectWithValue('Failed to update charts')
    }
  },
)

export const deleteDentalChart = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'api/charting/delete/dental_charting',
  async (chartId, { rejectWithValue }) => {
    if (!chartId) return rejectWithValue('Chart ID not found')
    try {
      const response = await deleteDentalChartById(chartId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to delete charts')
      }
      return chartId
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete charts'
      return rejectWithValue(message)
    }
  },
)

export const deletePerioChart = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'api/charting/delete/perio_charting',
  async (chartId, { rejectWithValue }) => {
    if (!chartId) return rejectWithValue('Chart ID not found')
    try {
      const response = await deletePerioChartById(chartId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to delete charts')
      }
      return chartId
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete charts'
      return rejectWithValue(message)
    }
  },
)
